import FirestoreService from '../../../services/FirestoreService';
import FunctionsService from '../../../services/FunctionsService';
import GameModel, { GameState } from '../models/GameModel';
import PlayerModel, { CreatePlayerViewModel } from '../models/PlayerModel';
import firebase from 'firebase/app';
import CreateGameViewModel from '../models/CreateGameViewModel';
import { classToPlain } from 'class-transformer';

export enum EventType {
  PLAYER_JOIN,
  PLAYER_LEAVE,
  PLAYER_POINT_INCREMENT,
  GAME_START,
  GAME_END,
}

function eventTypeToString(type: EventType) {
  switch (type) {
    case EventType.PLAYER_JOIN: {
      return 'PlayerJoinEvent';
    }
    case EventType.PLAYER_LEAVE: {
      return 'PlayerLeaveEvent';
    }
    case EventType.PLAYER_POINT_INCREMENT: {
      return 'PlayerPointIncrementEvent';
    }
    case EventType.GAME_START: {
      return 'GameStartEvent';
    }
    case EventType.GAME_END: {
      return 'GameEndEvent';
    }
  }
}

interface PlayerJoinEventPayload {
  player: PlayerModel;
}

interface PlayerLeaveEventPayload {
  player: PlayerModel;
}

interface PlayerPointIncrementEventPayload {
  currentValue: number;
  expectedNextValue: number;
}

interface GameStartEventPayload {
  previousState: GameState;
}

interface GameEndEventPayload {
  previousState: GameState;
  winner: PlayerModel | null;
}

type TGameEvent =
  | {
      type: EventType.PLAYER_JOIN;
      data: PlayerJoinEventPayload;
    }
  | {
      type: EventType.PLAYER_LEAVE;
      data: PlayerLeaveEventPayload;
    }
  | {
      type: EventType.PLAYER_POINT_INCREMENT;
      data: PlayerPointIncrementEventPayload;
    }
  | {
      type: EventType.GAME_START;
      data: GameStartEventPayload;
    }
  | {
      type: EventType.GAME_END;
      data: GameEndEventPayload;
    };

export default class GameData {
  firestoreService: FirestoreService;
  functionsService: FunctionsService;
  addGamePlayerCallable: firebase.functions.HttpsCallable;

  constructor(firestoreService: FirestoreService, functionsService: FunctionsService) {
    this.firestoreService = firestoreService;
    this.functionsService = functionsService;
    this.addGamePlayerCallable = this.functionsService.functions.httpsCallable('createGame');
  }

  streamGameDocument(gameId: string, observer: any) {
    return this.firestoreService.database
      .collection('game')
      .doc(gameId)
      .onSnapshot({
        next: (documentSnapshot) => {
          const updatedGameDetail = GameModel.fromJson({
            ...documentSnapshot.data(),
            id: documentSnapshot.id,
          });
          observer.next(updatedGameDetail);
        },
        error: observer.error,
        complete: observer.complete,
      });
  }

  streamGameCollection(observer: any) {
    return this.firestoreService.database.collection('game').onSnapshot({
      next: (querySnapshot) => {
        const updatedGameCollection = querySnapshot.docs.map((docSnapshot) =>
          GameModel.fromJson({ ...docSnapshot.data(), id: docSnapshot.id })
        );
        observer.next(updatedGameCollection);
      },
      error: observer.error,
      complete: observer.complete,
    });
  }

  streamGamePlayerCollection(gameId: string, observer: any) {
    return this.firestoreService.database
      .collection('game')
      .doc(gameId)
      .collection('player')
      .onSnapshot({
        next: (querySnapshot) => {
          const gamePlayerCollection = querySnapshot.docs.map((docSnapshot) =>
            PlayerModel.fromJson({ ...docSnapshot.data(), id: docSnapshot.id })
          );
          observer.next(gamePlayerCollection);
        },
        error: observer.error,
        complete: observer.complete,
      });
  }

  streamGameEventCollection(gameId: string, observer: any) {
    return this.firestoreService.database
      .collection('game')
      .doc(gameId)
      .collection('event')
      .limit(5)
      .orderBy('createdAt', 'desc')
      .onSnapshot({
        next: (querySnapshot) => {
          const gameEventCollection = querySnapshot.docs.map((docSnapshot) =>
            PlayerModel.fromJson({ ...docSnapshot.data(), id: docSnapshot.id })
          );
          observer.next(gameEventCollection);
        },
        error: observer.error,
        complete: observer.complete,
      });
  }

  async setGameName(game: GameModel, displayName: string) {
    return this.firestoreService.database.collection('game').doc(game.id).update({ displayName });
  }

  async createGamePlayer(game: GameModel, player: CreatePlayerViewModel) {
    const json = classToPlain(player);
    return this.firestoreService.database
      .collection('game')
      .doc(game.id)
      .collection('player')
      .add(json);
  }

  async deleteGamePlayer(game: GameModel, player: PlayerModel) {
    console.log('delete');
    return this.firestoreService.database
      .collection('game')
      .doc(game.id)
      .collection('player')
      .doc(player.id)
      .delete();
  }

  async createGameEvent(game: GameModel, event: TGameEvent) {
    console.log('event.data');
    console.log(event.data);
    return this.firestoreService.database
      .collection('game')
      .doc(game.id)
      .collection('event')
      .add({
        type: event.type,
        ...event.data,
        typeTitle: eventTypeToString(event.type),
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
  }

  async createGame(game: CreateGameViewModel) {
    const gameDataRef = await this.firestoreService.database.collection('game').add(game.toJson());
    const gameDocument = await gameDataRef.get();
    return GameModel.fromJson({ id: gameDocument.id, ...gameDocument.data() });
  }

  async getGame(gameId: string) {
    const gameDataRef = await this.firestoreService.database.collection('game').doc(gameId);
    const gameDocument = await gameDataRef.get();
    return GameModel.fromJson({ id: gameDocument.id, ...gameDocument.data() });
  }

  async incrementPlayerField(field: string, game: GameModel, player: PlayerModel, count: number) {
    return this.firestoreService.database
      .collection('game')
      .doc(game.id)
      .collection('player')
      .doc(player.id)
      .update({ [field]: firebase.firestore.FieldValue.increment(count) });
  }

  async setGameState(game: GameModel, state: GameState) {
    return this.firestoreService.database.collection('game').doc(game.id).update({ state });
  }
}
